import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { submitContactForm, checkExistingContact } from '../../../services/contactService';

const ServiceDetail = ({ service }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  if (!service) return null;

  // Ensure all required fields have default values
  const safeService = {
    ...service,
    hero: {
      headline: service.hero?.headline || '',
      subheadline: service.hero?.subheadline || '',
      ctaPrimary: {
        text: service.hero?.ctaPrimary?.text || 'Get Started',
        link: service.hero?.ctaPrimary?.link || '/contact'
      },
      ctaSecondary: {
        text: service.hero?.ctaSecondary?.text || 'Learn More',
        link: service.hero?.ctaSecondary?.link || '/about'
      },
      visual: {
        type: service.hero?.visual?.type || 'image',
        url: service.hero?.visual?.url || '',
        alt: service.hero?.visual?.alt || ''
      }
    },
    problem: {
      painPoints: service.problem?.painPoints || [],
      solution: {
        points: service.problem?.solution?.points || [],
        tagline: service.problem?.solution?.tagline || ''
      }
    },
    howItWorks: {
      headline: service.howItWorks?.headline || '',
      steps: service.howItWorks?.steps || []
    },
    features: {
      headline: service.features?.headline || '',
      items: service.features?.items || []
    },
    targetAudience: {
      headline: service.targetAudience?.headline || '',
      personas: service.targetAudience?.personas || []
    },
    pricing: {
      headline: service.pricing?.headline || '',
      packages: service.pricing?.packages || []
    },
    testimonials: (service.testimonials || []).map(testimonial => ({
      quote: testimonial.quote || '',
      author: testimonial.author || '',
      role: testimonial.role || '',
      company: testimonial.company || '',
      avatar: {
        url: testimonial.avatar?.url || '',
        alt: testimonial.avatar?.alt || ''
      }
    })),
    faq: service.faq || [],
    cta: {
      headline: service.cta?.headline || '',
      subheadline: service.cta?.subheadline || '',
      primary: {
        text: service.cta?.primary?.text || 'Get Started',
        link: service.cta?.primary?.link || '/contact'
      },
      secondary: {
        text: service.cta?.secondary?.text || 'Learn More',
        link: service.cta?.secondary?.link || '/about'
      }
    },
    booking: {
      type: service.booking?.type || 'calendly',
      calendlyUrl: service.booking?.calendlyUrl || '',
      formFields: service.booking?.formFields || []
    },
    meta: {
      title: service.meta?.title || service.title,
      description: service.meta?.description || '',
      keywords: service.meta?.keywords || []
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sticky CTA Header */}
      <div className={`sticky top-0 z-50 ${isDark ? 'bg-blue-700' : 'bg-blue-600'} text-white py-2 px-4`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <span className="font-semibold">⚡ Only 3 onboarding slots this week</span>
          <Link
            to={safeService.hero.ctaPrimary.link}
            className={`${isDark ? 'bg-gray-100 text-blue-700 hover:bg-white' : 'bg-white text-blue-600 hover:bg-blue-50'} px-4 py-2 rounded-md font-medium`}
          >
            {safeService.hero.ctaPrimary.text}
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="relative w-full h-96">
          {safeService.hero.visual?.url && (
            <img
              src={safeService.hero.visual.url}
              alt={safeService.hero.visual.alt || safeService.hero.headline}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center text-white p-4">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl font-extrabold text-center sm:text-5xl md:text-6xl"
            >
              {safeService.hero.headline}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-3 max-w-md mx-auto text-base sm:text-lg md:mt-5 md:text-xl md:max-w-3xl text-center"
            >
              {safeService.hero.subheadline}
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex justify-center space-x-4"
            >
              <Link
                to={safeService.hero.ctaPrimary.link}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                  isDark ? 'bg-green-700 hover:bg-green-600' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {safeService.hero.ctaPrimary.text}
              </Link>
              <Link
                to={safeService.hero.ctaSecondary.link}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                  isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {safeService.hero.ctaSecondary.text}
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Problem & Solution Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} p-8 rounded-lg shadow-lg`}>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>The Problem</h2>
              <ul className="space-y-4">
                {safeService.problem.painPoints.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">❌</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} p-8 rounded-lg shadow-lg`}>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Our Solution</h2>
              <ul className="space-y-4">
                {safeService.problem.solution.points.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {safeService.problem.solution.tagline}
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            {safeService.howItWorks.headline}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safeService.howItWorks.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} p-6 rounded-lg`}
              >
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">{step.icon}</span>
                  <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
                </div>
                <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            {safeService.features.headline}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {safeService.features.items.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-lg shadow-lg p-6`}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{feature.icon}</span>
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{feature.title}</h3>
                </div>
                <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Target Audience Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            {safeService.targetAudience.headline}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {safeService.targetAudience.personas.map((persona, index) => (
              <div key={index} className="flex items-center">
                <span className="text-2xl mr-3">{persona.icon}</span>
                <div>
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{persona.title}</h3>
                  <p className={isDark ? 'text-gray-300' : 'text-gray-500'}>{persona.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            {safeService.pricing.headline}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safeService.pricing.packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className={`${isDark ? 'bg-gray-700' : 'bg-white'} rounded-lg shadow-lg p-6`}
              >
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pkg.name}</h3>
                <p className={`mt-4 text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>${pkg.price}</p>
                <ul className="mt-6 space-y-4">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className="h-5 w-5 text-green-500 mr-2" />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link
                    to={pkg.cta?.link || '/contact'}
                    className={`block w-full text-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white ${
                      isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {pkg.cta?.text || 'Get Started'}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            What Our Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safeService.testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} p-6 rounded-lg`}
              >
                <blockquote className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  "{testimonial.quote}"
                </blockquote>
                <div className="mt-4 flex items-center">
                  {testimonial.avatar.url && (
                    <img
                      src={testimonial.avatar.url}
                      alt={testimonial.avatar.alt || testimonial.author}
                      className="h-10 w-10 rounded-full"
                    />
                  )}
                  <div className="ml-4">
                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {testimonial.author}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {testimonial.role}, {testimonial.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'} text-center mb-12`}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {safeService.faq.map((item, index) => (
              <div key={index} className={`${isDark ? 'bg-gray-700' : 'bg-white'} p-6 rounded-lg shadow`}>
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.question}</h3>
                <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className={`py-12 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {safeService.cta.headline}
            </h2>
            <p className={`mt-4 text-xl ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
              {safeService.cta.subheadline}
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <Link
                to={safeService.cta.primary.link}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                  isDark ? 'bg-green-700 hover:bg-green-600' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {safeService.cta.primary.text}
              </Link>
              <Link
                to={safeService.cta.secondary.link}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white ${
                  isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {safeService.cta.secondary.text}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Section */}
      {safeService.booking.type === 'form' && (
        <div className={`py-12 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`${isDark ? 'bg-gray-700' : 'bg-white'} p-8 rounded-lg shadow-lg`}>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>
                Request {safeService.title} 
              </h2>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
                  ⚠️ Please note: You can only submit one request per service within 7 days. 
                  If you have already submitted a request, please wait for our response.
                </p>
              </div>
              <form className="space-y-6" onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = {
                  name: formData.get('name'),
                  email: formData.get('email'),
                  phone: formData.get('phone'),
                  subject: `Request for ${safeService.title}`,
                  message: `Service Request Details:\n\n${formData.get('message')}\n\nRequested Service: ${safeService.title}`,
                  serviceId: safeService._id,
                  serviceName: safeService.title
                };
                
                try {
                  // First check for existing submission
                  const checkResponse = await checkExistingContact(data.email, data.message, data.serviceId);
                  console.log('### checkResponse', checkResponse);
                  if (checkResponse.hasExistingRequest) {
                    toast.error('You have already submitted a request for this service. Please wait for our response.');
                    return;
                  }

                  // If no existing submission, proceed with new submission
                  const response = await submitContactForm(data);
                  if (response.success) {
                    toast.success('Your request has been submitted successfully! We will contact you soon.');
                    e.target.reset();
                  } else {
                    toast.error(response.message || 'Failed to submit request');
                  }
                } catch (error) {
                  if (error.response?.status === 409) {
                    toast.error('You have already submitted a request for this service. Please wait for our response.');
                  } else {
                    toast.error(error.response?.data?.message || 'Failed to submit request. Please try again later.');
                  }
                }
              }}>
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white focus:border-blue-500 focus:ring-blue-500' 
                        : 'border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white focus:border-blue-500 focus:ring-blue-500' 
                        : 'border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white focus:border-blue-500 focus:ring-blue-500' 
                        : 'border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Additional Details *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white focus:border-blue-500 focus:ring-blue-500' 
                        : 'border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="Please provide any additional details about your request..."
                  />
                </div>

                <button
                  type="submit"
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isDark ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                >
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Calendly Section */}
      {safeService.booking.type === 'calendly' && safeService.booking.calendlyUrl && (
        <div className={`py-12 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} p-8 rounded-lg shadow-lg`}>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>Schedule a Consultation</h2>
              <div className="calendly-inline-widget" data-url={safeService.booking.calendlyUrl}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetail; 